import { CardContent, Grid, ListItem, ListItemText } from '@mui/material';
import { Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PmiCard } from 'shell/components/shared/card';
import PmiTitle from 'shell/components/shared/title';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonCivilStatusCrumbs } from 'civil-status/constants/breadcrumb';
import { ItemVM } from 'shell/view-models/item';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActWizard } from 'shell/models/act-wizard';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { transformerMenu } from 'civil-status/utils/transformer-menu';
import FormSubMenuPage from 'civil-status/components/sub-menu-page';
import { noop } from 'shell/utils/noop';
import { useFormik } from 'formik';
import { SubMenuFormVM } from 'civil-status/view-models/sub-menu-form';
import { DateTime } from 'luxon';
import { Typography } from '@mui/material';
import { useEventCallback } from '@mui/material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DataWizardAct } from 'civil-status/view-models/step-form';
import { STATES_ACT } from 'civil-status/constants/states-act';
import { AxiosResponse } from 'axios';

const SubMenuPage = (): React.ReactElement => {
  const params = useParams();
  const navigate = useNavigate();

  const basePath = `/stato-civile/${params.valueCat}/${params.idCat}/${params.sottoMenuName}/${params.idEvento}`;

  const [requests] = useHttpFetch<HttpResponse<ActWizard>, Array<ItemVM>>(
    `${__ACT_API_URL__}/wizard/questionario`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          codStep: params.idCat || undefined,
          idEvento: params.idEvento ? Number.parseInt(params.idEvento, 10) : undefined,
        },
      },
      transformer: transformerMenu,
    }
  );

  const requestsData = (requests.data || []) as Array<ItemVM>;

  const formContext = useFormik<SubMenuFormVM>({
    initialValues: {
      isRear: false,
      date: DateTime.now(),
      time: DateTime.now(),
    },
    onSubmit: noop,
  });

  const saveData = useHttpClient(`${__ACT_API_URL__}/wizard/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickCard = useEventCallback((item: ItemVM) => {
    const castedRequest = (requests as unknown as AxiosResponse<HttpResponse<ActWizard>>).data
      .payload;

    const date = formContext.values.date?.toFormat('yyyy-MM-dd') || '';
    const hours = formContext.values.time?.toFormat('THHmmssSSS') || '';
    const dataFormazioneAtto =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd THHmmssSSS') || undefined;

    const data: DataWizardAct = {
      questionarioEvento: [
        {
          key: {
            idEvento: params.idEvento ? Number.parseInt(params.idEvento, 10) : undefined,
            numProgressivoStep: 1,
          },
          cdStepSuccessivo: params.idCat || undefined,
          codStep: ' ',
          descBreveDomanda: 'Evento',
          descBreveRisposta: params.valueCat || undefined,
        },
        {
          key: {
            idEvento: params.idEvento ? Number.parseInt(params.idEvento, 10) : undefined,
            numProgressivoStep: 2,
          },
          cdStepSuccessivo: item.id?.toString() || undefined,
          codStep: params.idCat,
          descBreveDomanda: 'Tipo atto',
          descBreveRisposta: item.value || undefined,
        },
      ],
      statoCivile: {
        dataFormazioneAtto,
        tipoAtto: item.id?.toString(),
        // tipoEvento: params.valueCat || undefined,
        tipoEvento: params.idCat || undefined,
        tipoContenuto: castedRequest?.evento?.tipoContenuto || undefined,
        codiceStatoEvento: STATES_ACT.ID_STATE_BOZZA,
        key: {
          idEvento: params.idEvento ? Number.parseInt(params.idEvento, 10) : undefined,
        },
      },
      evento: {
        dataFormazioneAtto,
        tipoContenuto: castedRequest?.evento?.tipoContenuto || undefined,
        // tipoEvento: params.valueCat || undefined,
        tipoEvento: params.idCat || undefined,
        codiceStatoEvento: STATES_ACT.ID_STATE_BOZZA,
        key: {
          idEvento: params.idEvento ? Number.parseInt(params.idEvento, 10) : undefined,
        },
      },
    };

    (
      saveData({
        data,
      }) as unknown as ResponsePromise<void>
    ).then(() => {
      navigate(`${basePath}/${item.value}/${item.id}`);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs
        items={[
          ...CommonCivilStatusCrumbs,
          {
            name: 'Tipo atto',
            path: basePath,
          },
        ]}
      />
      <PmiTitle title="Tipo atto" paddingBottom={1} />
      <div className="pmi-mb-4">
        <Typography component="div" fontSize=".9rem" color="primary">
          {"Seleziona l'area su cui operare"}
        </Typography>
      </div>
      <div className="pmi-mb-4">
        <FormSubMenuPage
          values={formContext.values}
          onChange={formContext.setFieldValue}
          onChanges={formContext.setValues}
        />
      </div>
      <Grid container spacing={2}>
        {requestsData.map((item) => (
          <Grid item xs={12} md={4} key={`${item.value}-${item.id}`}>
            <span onClick={() => onClickCard(item)} style={{ cursor: 'pointer' }}>
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary={item.value} />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </span>
          </Grid>
        ))}
      </Grid>
    </Fragment>
  );
};

SubMenuPage.displayName = 'Sub Menu Page';

export default SubMenuPage;
